import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, X } from 'lucide-react';
import contactsApi from '../api/contacts';
import { getProperties } from '../api/properties';
import { fetchUsageSafe } from '../api/usage';
import { useSubmissionLock } from '../hooks/useSubmissionLock';

const initialState = {
  firstName: '',
  lastName: '',
  email: '',
  relationship: '',
  phone: '',
  company: '',
  property: '',
  notes: '',
};

const relationships = [
  { value: '', label: 'Select relationship' },

  { value: 'Tenant', label: 'Tenant' },
  { value: 'Emergency Contact', label: 'Emergency Contact' },

  { value: 'Agent', label: 'Agent' },
  { value: 'Legal', label: 'Legal' },
  { value: 'Accountant', label: 'Accountant' },

  { value: 'Maintenance', label: 'Maintenance' },
  { value: 'Electrician', label: 'Electrician' },
  { value: 'Plumber', label: 'Plumber' },
  { value: 'Cleaner', label: 'Cleaner' },
  { value: 'Contractor', label: 'Contractor' },

  { value: 'Other', label: 'Other' },
];

function contactLimitCopy(usage) {
  const target = usage?.isPlus && !usage?.isEnhanced ? 'Beem Enhanced' : 'Beem Plus';
  return `Contact limit reached (${usage?.contactLimit ?? 0}). Upgrade to ${target} to add more contacts.`;
}

function AddContactDrawer({ onSave, onClose }) {
  const [form, setForm] = useState(initialState);
  const [saving, setSaving] = useState(false);
  const [properties, setProperties] = useState([]);
  const [loadingProperties, setLoadingProperties] = useState(true);
  const [usage, setUsage] = useState(null);
  const { run: runSubmission, isPending } = useSubmissionLock();
  const navigate = useNavigate();

  useEffect(() => {
    fetchUsageSafe().then(setUsage);
  }, []);

  useEffect(() => {
    getProperties()
      .then((res) => {
        setProperties(res.data || []);
      })
      .catch((err) => {
        console.error('Failed to load properties:', err);
      })
      .finally(() => {
        setLoadingProperties(false);
      });
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const isFormValid =
    form.firstName.trim() &&
    form.lastName.trim() &&
    form.email.trim() &&
    form.relationship.trim() &&
    form.phone.trim();

  const contactLimitReached =
    usage && typeof usage.contactCount === 'number' && typeof usage.contactLimit === 'number'
      ? usage.contactCount >= usage.contactLimit
      : false;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving || isPending('contact:create')) return;

    if (!isFormValid) return;

    if (contactLimitReached) {
      alert(contactLimitCopy(usage));
      return;
    }

    await runSubmission('contact:create', async () => {
      setSaving(true);

      try {
        const newContact = await contactsApi.createContact({
          ...form,
          name: `${form.firstName} ${form.lastName}`.trim(),
          propertyId: form.property || null,
        });

        fetchUsageSafe().then(setUsage);

        if (onSave) onSave(newContact);
        alert('Contact added');
        setForm(initialState);
        navigate(0);
      } catch (err) {
        console.error('Failed to create contact:', err);

        const code = err?.response?.data?.code;
        if (code === 'CONTACT_LIMIT_REACHED') {
          const limit = err?.response?.data?.limit ?? usage?.contactLimit ?? 0;
          alert(contactLimitCopy({ ...usage, contactLimit: limit }));
          fetchUsageSafe().then(setUsage);
        } else {
          alert('Error creating contact');
        }
      } finally {
        setSaving(false);
      }
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative ml-auto h-full w-full max-w-xl overflow-y-auto bg-[linear-gradient(to_bottom,_#2A2A2A,_#1A1A1A)] p-8 text-white">
        <button onClick={onClose} className="absolute top-4 right-4 text-white hover:text-[#AF9B8A]">
          <X className="w-6 h-6" />
        </button>
        <h2 className="text-2xl font-bold mb-6">Add Contact</h2>

        {contactLimitReached && (
          <div className="mb-4 p-3 rounded-lg bg-white/10 border border-white/10 text-white/80 text-sm">
            {contactLimitCopy(usage)}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="flex gap-4">
            <div className="flex-1">
              <label className="block mb-1 font-semibold">First Name</label>
              <input
                type="text"
                name="firstName"
                value={form.firstName}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-white"
                placeholder="First name"
              />
            </div>
            <div className="flex-1">
              <label className="block mb-1 font-semibold">Last Name</label>
              <input
                type="text"
                name="lastName"
                value={form.lastName}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-white"
                placeholder="Last name"
              />
            </div>
          </div>

          <div>
            <label className="block mb-1 font-semibold">Email</label>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-white"
              placeholder="Email address"
            />
          </div>

          <div className="flex gap-4">
            <div className="flex-1 relative">
              <label className="block mb-1 font-semibold">Relationship</label>
              <select
                name="relationship"
                value={form.relationship}
                onChange={handleChange}
                required
                className="appearance-none w-full px-4 py-2 rounded-lg text-white bg-white/10 border border-white/10"
              >
                {relationships.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-4 top-10 w-4 h-4 text-white/70 pointer-events-none" />
            </div>
            <div className="flex-1">
              <label className="block mb-1 font-semibold">Phone</label>
              <input
                type="tel"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-white"
                placeholder="Phone number"
              />
            </div>
          </div>

          <div>
            <label className="block mb-1 font-semibold">Company</label>
            <input
              type="text"
              name="company"
              value={form.company}
              onChange={handleChange}
              className="w-full px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-white"
              placeholder="Company (optional)"
            />
          </div>

          <div className="relative">
            <label className="block mb-1 font-semibold">Property</label>
            <select
              name="property"
              value={form.property}
              onChange={handleChange}
              disabled={loadingProperties}
              className="appearance-none w-full px-4 py-2 rounded-lg text-white bg-white/10 border border-white/10"
            >
              <option value="">
                {loadingProperties ? 'Loading properties...' : 'Select property (optional)'}
              </option>
              {properties.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-4 top-11 w-4 h-4 text-white/70 pointer-events-none" />
          </div>

          <div>
            <label className="block mb-1 font-semibold">Notes</label>
            <textarea
              name="notes"
              value={form.notes}
              onChange={handleChange}
              rows={3}
              className="w-full px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-white"
              placeholder="Additional notes"
            />
          </div>

          <button
            type="submit"
            disabled={!isFormValid || saving || contactLimitReached}
            className="w-full py-3 rounded-lg bg-[#AF9B8A] text-white font-semibold hover:bg-[#AF9B8A]/80 transition cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? 'Adding contact...' : 'Add Contact'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default AddContactDrawer;
